import React, { useEffect, useState } from "react"
import {
  Box,
  Button,
  DialogContent,
  DialogTitle,
  Link,
  LocationIcon,
  SelectedIcon,
  Tile,
  Typography
} from "@carrotfertility/carotene-core"
import { FormattedMessage, useIntl } from "react-intl"
import { DialogActionsBar } from "@carrotfertility/carotene-core-x"
import { CarrotMatchResultStepCloseButton } from "#/components/carrot-match-fertility-clinic-precision-matching/steps/CarrotMatchResultStepCloseButton"
import { useCarrotMatch } from "#/components/carrot-match-fertility-clinic-precision-matching/context/CarrotMatchContext"
import { useGetLocalizedDistanceStringWithUnits } from "#/pages/provider-finder/hooks/useGetLocalizedDistanceStringWithUnits"
import { useCurrentUser } from "#/components/context/user/UserContext"
import { useNavAvailability } from "#/services/feature-availability/useNavAvailability"
import { getMatchAttributesOrder } from "#/components/carrot-match-fertility-clinic-precision-matching/utils/matchAttributesOrder"
import {
  CarrotMatchAnswer,
  FertilityTreatment,
  Questions,
  WhatMatters
} from "#/components/carrot-match-fertility-clinic-precision-matching/utils/carrotMatchTypes"
import { useStateMachine } from "#/components/context/stateMachine/StateMachineV2"
import { MemberActions } from "#/services/memberActionTracking/memberActions"
import { useMemberActionTracking } from "#/services/memberActionTracking/useMemberActionTracking"
import { useGetFundsAdministration } from "#/components/bank-details/hooks/useGetFundsAdministration"
import { Paths } from "#/utils/Paths"

const DirectionsLink = ({
  address,
  searchLocation,
  providerUuid
}: {
  address: string
  searchLocation: string
  providerUuid: number
}): JSX.Element => {
  const { mutate: trackMemberAction } = useMemberActionTracking()

  const getGoogleDirections = (address: string): string => {
    // The structure of the url is `/maps/dir/<starting point>/<destination>`
    const googleBaseUrl = "https://www.google.com/maps/dir"
    const encodedSearchLocation = encodeURIComponent(searchLocation)
    const encodedAddress = encodeURIComponent(address)
    return `${googleBaseUrl}/${encodedSearchLocation}/${encodedAddress}`
  }
  return (
    <Link
      data-id="carrot-match-directions-link"
      href={getGoogleDirections(address)}
      target="_blank"
      color="inherit"
      onClick={() => {
        trackMemberAction({
          memberActionTaken: MemberActions.CARROT_MATCH_RESULT_INTERACTION_DIRECTIONS,
          metaData: {
            providerUuid
          }
        })
      }}
    >
      <Typography variant="body2">
        <FormattedMessage
          defaultMessage="Directions"
          description="Clicking on the directions link will open up Google Maps to get the directions to this provider's location"
        />
      </Typography>
    </Link>
  )
}

export function ResultStep(): JSX.Element {
  const { send } = useStateMachine()
  const { carrotMatchInteraction, updateCarrotMatchInteraction } = useCarrotMatch()
  const { location, questionsAndAnswers, matchedProviders } = carrotMatchInteraction
  const intl = useIntl()
  const { company } = useCurrentUser()
  const { countryCode: companyCountryCode } = company

  const [currentProviderIndex, setCurrentProviderIndex] = useState(0)
  const [matchAttributes, setMatchAttributes] = useState<any[]>([])

  const { mutate: trackMemberAction } = useMemberActionTracking()

  const treatmentTypeSelected = questionsAndAnswers.find(
    ({ question }) => question === Questions.TREATMENT_TYPE_QUESTION
  )?.answer as FertilityTreatment

  const specializationSelected = questionsAndAnswers.find(
    ({ question }) => question === Questions.CLINIC_SPECIALIZATION_QUESTION
  )?.answer as CarrotMatchAnswer[]

  const specialServiceSelected = questionsAndAnswers.find(
    ({ question }) => question === Questions.SPECIAL_SERVICES_QUESTION
  )?.answer as CarrotMatchAnswer[]

  const selectedCostSensitivity = questionsAndAnswers.some(
    ({ question, answer }) =>
      question === Questions.WHAT_MATTERS_TO_XYZ_QUESTION &&
      Array.isArray(answer) &&
      answer.includes(WhatMatters.COST_DISCOUNTS)
  )

  const currentProvider = matchedProviders?.[currentProviderIndex] ?? null
  const isCarrotPartner = currentProvider?.carrotPartner
  const fundsAdministration = useGetFundsAdministration()
  const showDirectPay =
    currentProvider?.acceptsDirectPay && fundsAdministration.data?.reimbursement?.isCarrotDirectPayEnabled

  const localizedProviderDistance = useGetLocalizedDistanceStringWithUnits({
    distanceInKm: currentProvider?.distanceInKm,
    countryCode: companyCountryCode,
    numberOfFractionDigits: 1
  })

  useEffect(() => {
    updateCarrotMatchInteraction({
      lastMatchViewedBeforeLeavingFlowProviderUuid: currentProvider?.providerUuid,
      lastMatchViewedBeforeLeavingFlowIndex: currentProviderIndex
    })
    const updatedAttributes = getMatchAttributesOrder({
      specializationSelected,
      treatmentTypeSelected,
      specialServiceSelected,
      isCarrotPartner,
      selectedCostSensitivity,
      showDirectPay
    })

    setMatchAttributes(updatedAttributes)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- See https://carrotfertility.atlassian.net/wiki/spaces/PE/pages/2050295461/Remove+Build+Warnings#react-hooks%2Fexhaustive-deps
  }, [
    specializationSelected,
    treatmentTypeSelected,
    specialServiceSelected,
    isCarrotPartner,
    selectedCostSensitivity,
    currentProvider,
    currentProviderIndex,
    showDirectPay
  ])

  const getSeekingCareDialogTitle = () => {
    const titles = ["Best match", "Great match", "Good match"]
    return matchedProviders?.length > 0 ? titles[currentProviderIndex] : titles[0]
  }
  const isNextMatchAvailable = matchedProviders?.length > 1 && currentProviderIndex < matchedProviders?.length - 1

  const handleNextMatch = () => {
    if (currentProviderIndex < matchedProviders?.length - 1) {
      setCurrentProviderIndex(currentProviderIndex + 1)
    } else {
      send("")
    }
  }

  const handlePreviousMatch = () => {
    if (currentProviderIndex > 0) {
      setCurrentProviderIndex(currentProviderIndex - 1)
    }
  }

  const hasProviderFinder = useNavAvailability().show.providerFinder
  const requestAppointmentButton = (
    <Button
      data-id="carrot-match-request-appointment-button"
      variant="contained"
      size="small"
      color="primary"
      component="a"
      fullWidth={false}
      sx={{ inlineSize: "fit-content" }}
      href={`${Paths.PROVIDER_FINDER}/requestappointment/${encodeURIComponent(currentProvider?.providerName)}/${
        currentProvider?.providerUuid
      }`}
      title={intl.formatMessage({ defaultMessage: "Contact our team to book an appointment" })}
      target="_blank"
      onClick={() => {
        trackMemberAction({
          memberActionTaken: MemberActions.CARROT_MATCH_RESULT_INTERACTION_REQUEST_APPOINTMENT,
          metaData: {
            providerUuid: currentProvider?.providerUuid
          }
        })
      }}
    >
      <FormattedMessage
        defaultMessage="Request appointment"
        description="Clicking on the Request appointment button will redirect you to directions on how to book an appointment at a provider"
      />
    </Button>
  )

  const address = (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
        gap: (theme) => theme.spacing(theme.tokens.spacing.xxs),
        alignItems: "start"
      }}
    >
      <LocationIcon fontSize="inherit" role="presentation" color="inherit" />
      <Typography variant="body2" color="inherit">
        <FormattedMessage
          defaultMessage="{distance} • {address}"
          values={{
            distance: localizedProviderDistance,
            address: currentProvider?.address
          }}
        />
      </Typography>
    </Box>
  )

  const providerDetails = (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
        gap: (theme) => `${theme.spacing(theme.tokens.spacing.xs)} ${theme.spacing(theme.tokens.spacing.lg)}`,
        whiteSpace: "nowrap",
        flexWrap: "wrap"
      }}
    >
      {currentProvider?.phone && (
        <Link
          data-id="carrot-match-provider-phone-link"
          dir="ltr"
          href={`tel:${currentProvider?.phone}`}
          color="inherit"
          onClick={() => {
            trackMemberAction({
              memberActionTaken: MemberActions.CARROT_MATCH_RESULT_INTERACTION_PHONE_NUMBER,
              metaData: {
                providerUuid: currentProvider?.providerUuid
              }
            })
          }}
        >
          <Typography variant="body2">{currentProvider?.phone}</Typography>
        </Link>
      )}
      {currentProvider?.website && (
        <Link
          data-id="carrot-match-provider-website-link"
          href={currentProvider?.website}
          target="_blank"
          rel="noreferrer noopener"
          color="inherit"
          onClick={() => {
            trackMemberAction({
              memberActionTaken: MemberActions.CARROT_MATCH_RESULT_INTERACTION_WEBSITE,
              metaData: {
                providerUuid: currentProvider?.providerUuid
              }
            })
          }}
        >
          <Typography variant="body2">
            <FormattedMessage defaultMessage="Website" />
          </Typography>
        </Link>
      )}
      {currentProvider?.address && (
        <DirectionsLink
          address={currentProvider?.address}
          searchLocation={`${location.lat},${location.long}`}
          providerUuid={currentProvider?.providerUuid}
        />
      )}
    </Box>
  )

  return (
    <>
      <Box>
        <CarrotMatchResultStepCloseButton />
        <Box sx={{ display: "flex", flexDirection: "column", gap: (theme) => theme.spacing(theme.tokens.spacing.md) }}>
          <DialogTitle id="match-heading" sx={{ paddingBlockEnd: 0 }}>
            {getSeekingCareDialogTitle()}
          </DialogTitle>
          <DialogContent>
            {matchedProviders?.length > 0 && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: (theme) => theme.spacing(theme.tokens.spacing.md)
                }}
              >
                <Box
                  key={currentProviderIndex}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: (theme) => theme.spacing(theme.tokens.spacing.md)
                  }}
                >
                  <Typography color="inherit">{currentProvider?.providerName}</Typography>
                  {address}
                  {providerDetails}
                </Box>
                {isCarrotPartner && hasProviderFinder && requestAppointmentButton}
                <Tile
                  sx={{
                    padding: (theme) =>
                      `${theme.spacing(theme.tokens.spacing.md)} ${theme.spacing(theme.tokens.spacing.lg)}`
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{ marginBlockEnd: (theme) => theme.spacing(theme.tokens.spacing.xs) }}
                  >
                    <FormattedMessage defaultMessage="Matches your preferences" />
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: (theme) => theme.spacing(theme.tokens.spacing.xs),
                      flexWrap: "wrap"
                    }}
                  >
                    {matchAttributes.map((attribute) => (
                      <Box
                        key={attribute.key}
                        sx={{
                          display: "flex",
                          flexDirection: "row",
                          alignItems: "center",
                          gap: (theme) => theme.tokens.spacing.xxxs,
                          color: (theme) => theme.palette.text.tertiary
                        }}
                      >
                        <SelectedIcon fontSize="small" color="inherit" />
                        <Typography variant="body2" color={(theme) => theme.palette.text.secondary} fontSize="0.75rem">
                          {intl.formatMessage(attribute.description)}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Tile>
              </Box>
            )}
          </DialogContent>
          <DialogActionsBar
            primaryAction={
              isNextMatchAvailable && (
                <Button
                  data-id="carrot-match-result-next-step-button"
                  data-button-content={isNextMatchAvailable ? "next-match" : "close"}
                  variant="outlined"
                  color="secondary"
                  type="button"
                  onClick={handleNextMatch}
                >
                  <FormattedMessage defaultMessage="Next match" />
                </Button>
              )
            }
            tertiaryAction={
              currentProviderIndex > 0 && (
                <Button
                  data-id="carrot-match-result-previous-match-button"
                  onClick={handlePreviousMatch}
                  variant="outlined"
                  color="secondary"
                >
                  <FormattedMessage defaultMessage="Previous match" />
                </Button>
              )
            }
            sx={{ paddingBlockStart: 0 }}
          />
        </Box>
      </Box>
    </>
  )
}
